import Link from 'next/link';

import CloudOutlinedIcon from '@mui/icons-material/CloudOutlined';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';

import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import Chip from '@mui/material/Chip';
import CardContent from '@mui/material/CardContent';
import Grid from '@mui/material/Grid';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';

import MainLayout from '@/components/layouts/mainlayout';
import PageHeader from '@/components/layouts/PageHeader';
import { APP_TITLE_COLOR } from '@/components/layouts/brandSurface';
import { useTenantSurveyProgress } from '@/hooks/useTenantSurveyProgress';

const BENEFIT_CARDS = [
  {
    title: 'Multi-Cloud',
    description: 'Entenda a distribuição de workloads no mercado.',
    Icon: CloudOutlinedIcon,
  },
  {
    title: 'Benchmark',
    description: (
      <>
        Compare seu <em>Unit Economics</em> com empresas do mesmo setor e tamanho.
      </>
    ),
    Icon: TrendingUpIcon,
  },
  {
    title: 'Insights IA',
    description:
      'Descubra quais modelos de IA estão gerando ROI e recomendações personalizadas.',
    Icon: AutoAwesomeIcon,
  },
];

export default function HomePage() {
  const tenantProgressQuery = useTenantSurveyProgress('radar-da-nuvem-2026');
  const techCompleted = tenantProgressQuery.data?.tech_completed ?? false;
  const financeCompleted = tenantProgressQuery.data?.finance_completed ?? false;
  const allTracksCompleted = tenantProgressQuery.data?.all_completed ?? false;
  const hasAnyCompletedTrack = techCompleted || financeCompleted;

  const missingTrackLabel =
    techCompleted && !financeCompleted
      ? 'Financeira'
      : financeCompleted && !techCompleted
        ? 'Técnica'
        : null;

  const statusText = tenantProgressQuery.isLoading
    ? 'Carregando status da pesquisa da sua empresa...'
    : allTracksCompleted
      ? 'Sua empresa já concluiu as trilhas Técnica e Financeira. Benchmark e report estão liberados.'
      : techCompleted && !financeCompleted
        ? 'Sua empresa já concluiu a trilha Técnica. Para liberar o benchmark completo, falta a trilha Financeira.'
        : financeCompleted && !techCompleted
          ? 'Sua empresa já concluiu a trilha Financeira. Para liberar o benchmark completo, falta a trilha Técnica.'
          : 'Sua empresa ainda não concluiu nenhuma das trilhas. Responda pelo menos uma para liberar o report.';

  const primaryCta = tenantProgressQuery.isLoading
    ? { label: 'Carregando...', href: '/pesquisa', disabled: true }
    : hasAnyCompletedTrack
      ? { label: 'Ver Report 2025', href: '/report-completo-2025', disabled: false }
      : { label: 'Iniciar Pesquisa →', href: '/pesquisa', disabled: false };

  const secondaryCta = tenantProgressQuery.isLoading
    ? { label: 'Carregando...', href: '/benchmark', disabled: true, tooltip: '' }
    : allTracksCompleted
      ? { label: 'Ver Benchmark', href: '/benchmark', disabled: false, tooltip: '' }
      : hasAnyCompletedTrack
        ? {
            label: missingTrackLabel
              ? `Continuar trilha ${missingTrackLabel}`
              : 'Continuar Pesquisa',
            href: '/pesquisa',
            disabled: false,
            tooltip: '',
          }
        : {
            label: 'Ver Benchmark',
            href: '/benchmark',
            disabled: true,
            tooltip:
              'É necessário responder a pesquisa primeiro para liberar o benchmark.',
          };

  return (
    <MainLayout>
      <PageHeader
        size='large'
        title={
          <>
            <Box component='span' sx={{ color: APP_TITLE_COLOR }}>
              Radar da{' '}
            </Box>
            <Box component='span' sx={{ color: 'primary.main' }}>
              Nuvem
            </Box>
          </>
        }
        subtitle='Compare seus custos de cloud com +500 empresas brasileiras. Descubra oportunidades de otimização e benchmarks do mercado.'
        badge={
          <Chip
            label='Edição 2026'
            size='small'
            sx={{
              fontWeight: 600,
              bgcolor: 'primary.highlight',
              color: 'primary.main',
              border: '1px solid',
              borderColor: 'primary.lighter',
            }}
          />
        }
        breadcrumbs={[{ label: 'Início' }]}
      />

      <Box sx={{ width: '100%', pb: 3 }}>
        <Typography
          variant='body-medium'
          color='text.secondary'
          fontStyle='italic'
          sx={{ mb: 3 }}
        >
          O benchmark é mais preciso quando feito a quatro mãos (Tech + Finanças).
          Você pode convidar seu par durante o processo.
        </Typography>

        <Grid container spacing={1.5} sx={{ mb: 3 }}>
          <Grid item xs={12} sm='auto'>
            <Button
              component={Link}
              href={primaryCta.href}
              variant='contained'
              size='large'
              color='primary'
              disabled={primaryCta.disabled}
            >
              {primaryCta.label}
            </Button>
          </Grid>
          <Grid item xs={12} sm='auto'>
            {secondaryCta.disabled ? (
              <Tooltip title={secondaryCta.tooltip} arrow>
                <Box sx={{ display: 'inline-flex' }}>
                  <Button
                    component={Link}
                    href={secondaryCta.href}
                    variant='outlined'
                    size='large'
                    color='primary'
                    disabled
                  >
                    {secondaryCta.label}
                  </Button>
                </Box>
              </Tooltip>
            ) : (
              <Button
                component={Link}
                href={secondaryCta.href}
                variant='outlined'
                size='large'
                color='primary'
              >
                {secondaryCta.label}
              </Button>
            )}
          </Grid>
        </Grid>

        <Alert severity='info' sx={{ mb: 3, width: '100%' }}>
          {statusText}
        </Alert>

        <Grid container spacing={2}>
          {BENEFIT_CARDS.map((card) => {
            const Icon = card.Icon;
            return (
              <Grid item xs={12} md={4} key={card.title} sx={{ display: 'flex' }}>
                <Card
                  variant='outlined'
                  sx={{
                    width: '100%',
                    height: '100%',
                    borderColor: 'primary.highlight',
                    transition: 'border-color 0.2s',
                    '&:hover': {
                      borderColor: 'primary.lighter',
                    },
                  }}
                >
                  <CardContent sx={{ textAlign: 'left', py: 3 }}>
                    <Box
                      sx={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        width: 56,
                        height: 56,
                        borderRadius: 2,
                        bgcolor: 'primary.highlight',
                        mb: 2,
                      }}
                    >
                      <Icon
                        sx={{ fontSize: 28, color: 'primary.main' }}
                        aria-hidden
                      />
                    </Box>
                    <Typography
                      variant='title-medium'
                      gutterBottom
                      color='text.primary'
                    >
                      {card.title}
                    </Typography>
                    <Typography variant='body-medium' color='text.secondary'>
                      {card.description}
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </MainLayout>
  );
}
